import { questionTextToPlainText } from 'utils/questionMarkdown';
import { useCallback, useContext, useEffect, useState, useRef } from 'react';
import { AnswerReferenceContext } from 'utils/answerReferences';
import type { ResponsePayload } from '../../../types';
import { TextOrAudioResponseQuestion } from '../../questions/TextOrAudioResponseQuestion';
import { registerQuestionRenderer, type QuestionRendererComponent } from './registry';

const sanitizeText = (value: string) => value.trim();

const TextOrAudioRenderer: QuestionRendererComponent = ({
  question,
  existingResponse,
  isSubmitting,
  onDraftChange,
  onHasUserInputChange,
  onValidityChange,
  onExtrasChange,
}) => {
  const answerReferences = useContext(AnswerReferenceContext);
  const questionId = question?.id ?? existingResponse?.question ?? -1;
  const prevQuestionIdRef = useRef(questionId);

  const [textValue, setTextValue] = useState<string>(() => existingResponse?.text_value ?? '');
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);

  const emit = useCallback(
    (text: string, audio: Blob | null) => {
      const trimmed = sanitizeText(text);
      const hasText = trimmed.length > 0;
      const hasAudio = Boolean(audio);
      const hasExistingAudio = Boolean(existingResponse?.audio_url);
      const hasInput = hasText || hasAudio || hasExistingAudio;

      onHasUserInputChange?.(hasInput);
      onValidityChange?.(hasInput);
      onExtrasChange?.({ audioBlob: audio });

      if (!hasText) {
        onDraftChange(null);
        return;
      }

      const payload: ResponsePayload = {
        question: questionId,
        text_value: text,
      };

      onDraftChange(payload);
    },
    [existingResponse, onDraftChange, onHasUserInputChange, onValidityChange, onExtrasChange, questionId],
  );

  // Only reset when navigating to a different question
  useEffect(() => {
    if (prevQuestionIdRef.current !== questionId) {
      const initialText = existingResponse?.text_value ?? '';
      setTextValue(initialText);
      setAudioBlob(null);
      emit(initialText, null);
      prevQuestionIdRef.current = questionId;
    }
  }, [questionId, existingResponse?.text_value, emit]);

  const handleTextChange = (value: string) => {
    setTextValue(value);
    emit(value, audioBlob);
  };

  const handleAudioChange = (blob: Blob | null, transcription?: string) => {
    if (blob) {
      setAudioBlob(blob);
      const nextText = transcription ?? textValue;
      setTextValue(nextText);
      emit(nextText, blob);
    } else {
      setAudioBlob(null);
      emit(textValue, null);
    }
  };

  return (
    <TextOrAudioResponseQuestion
      label={questionTextToPlainText(question.text, { answerReferences })}
      inputTextValue={textValue}
      audioBlob={audioBlob}
      onTextChange={handleTextChange}
      onAudioChange={handleAudioChange}
      isSubmitting={isSubmitting}
    />
  );
};

registerQuestionRenderer(['text_or_audio'], TextOrAudioRenderer);
